import React from "react";

/** Ответ ассистента с минимальной разметкой (#401).
 *
 *  Модель размечает ответы markdown-ом — так она обучена, и запрет в промте маленькая
 *  модель соблюдает через раз (проверено на #389 и #400). Интерфейс показывал разметку
 *  буквально: «**ANYskin рейтинг**: C» со звёздочками, списки — строками с дефисами.
 *  Чиним там, где дефект.
 *
 *  Свой разбор вместо библиотеки: нужно пять конструкций из полусотни, а markdown-рендерер
 *  тянет в бандл десятки килобайт (бюджет entry-чанка 85 КБ) и почти всегда просит
 *  `dangerouslySetInnerHTML`. Здесь текст модели никогда не становится HTML — только
 *  React-узлами, поэтому разметка из ответа не может ничего выполнить.
 *
 *  Ссылки намеренно НЕ кликабельные: адрес в ответе генеративной модели может вести куда
 *  угодно, а брокер работает с деньгами клиента. Показываем текстом — человек решит сам. */

type Props = { text: string; className?: string };

// Курсив требует непробельных символов вплотную к звёздочкам — как в CommonMark. Иначе
// «сумма 2 * 3 и срок 4 * 5» превращалась в курсив «3 и срок 4»: в брокерском чате
// звёздочка чаще знак умножения, чем разметка.
const INLINE = /(\*\*[^*\n]+\*\*|`[^`\n]+`|\*\S[^*\n]*?\S\*|\*\S\*|_\S[^_\n]*?\S_)/g;

/** Жирное, курсив и код внутри строки. */
function inline(src: string, key: string): React.ReactNode[] {
  return src.split(INLINE).filter(Boolean).map((part, i) => {
    const k = `${key}-${i}`;
    if (part.length > 4 && part.startsWith("**") && part.endsWith("**"))
      return <b key={k}>{part.slice(2, -2)}</b>;
    if (part.length > 2 && part.startsWith("`") && part.endsWith("`"))
      return <code key={k} className="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800 font-mono text-[0.95em]">{part.slice(1, -1)}</code>;
    if (part.length > 2 && ((part.startsWith("*") && part.endsWith("*"))
                            || (part.startsWith("_") && part.endsWith("_"))))
      return <i key={k}>{part.slice(1, -1)}</i>;
    return <React.Fragment key={k}>{part}</React.Fragment>;
  });
}

type Block =
  | { kind: "p"; lines: string[] }
  | { kind: "h"; text: string }
  | { kind: "ul"; items: string[] }
  | { kind: "ol"; items: string[] };

/** Строки в блоки: абзацы, заголовки и списки. Таблицы и цитаты модель в этом чате не
 *  выдаёт — для них правил нет, они останутся обычным текстом. */
function parse(text: string): Block[] {
  const out: Block[] = [];
  for (const raw of (text || "").split("\n")) {
    const line = raw.trimEnd();
    const head = /^#{1,6}\s+(.+)$/.exec(line);
    const bullet = /^\s*[-*•]\s+(.+)$/.exec(line);
    const numbered = /^\s*\d+[.)]\s+(.+)$/.exec(line);
    const last = out[out.length - 1];

    if (head) { out.push({ kind: "h", text: head[1] }); continue; }
    if (bullet) {
      if (last?.kind === "ul") last.items.push(bullet[1]);
      else out.push({ kind: "ul", items: [bullet[1]] });
      continue;
    }
    if (numbered) {
      if (last?.kind === "ol") last.items.push(numbered[1]);
      else out.push({ kind: "ol", items: [numbered[1]] });
      continue;
    }
    if (!line.trim()) { if (last?.kind === "p") out.push({ kind: "p", lines: [] }); continue; }
    // Перенос внутри абзаца модель ставит осмысленно — сохраняем его, как делал
    // whitespace-pre-line до этой правки.
    if (last?.kind === "p" && last.lines.length) last.lines.push(line);
    else out.push({ kind: "p", lines: [line] });
  }
  return out.filter((b) => b.kind !== "p" || b.lines.length);
}

export default function AssistantText({ text, className = "" }: Props) {
  const blocks = React.useMemo(() => parse(text), [text]);
  if (!blocks.length) return null;
  return (
    <div className={`${className} space-y-2`}>
      {blocks.map((b, i) => {
        if (b.kind === "h")
          return <div key={i} className="font-bold">{inline(b.text, `h${i}`)}</div>;
        if (b.kind === "ul")
          return (
            <ul key={i} className="list-disc pl-5 space-y-0.5">
              {b.items.map((it, j) => <li key={j}>{inline(it, `u${i}-${j}`)}</li>)}
            </ul>
          );
        if (b.kind === "ol")
          return (
            <ol key={i} className="list-decimal pl-5 space-y-0.5">
              {b.items.map((it, j) => <li key={j}>{inline(it, `o${i}-${j}`)}</li>)}
            </ol>
          );
        return (
          <p key={i}>
            {b.lines.map((ln, j) => (
              <React.Fragment key={j}>
                {j > 0 && <br />}
                {inline(ln, `p${i}-${j}`)}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}
